import { Box, Card, CardContent, Typography, Alert, ToggleButtonGroup, ToggleButton, TextField, Button } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useAxiosPrivate from "../hooks/useAxiosPrivate";

// Registra le scale e i componenti necessari
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SensorThChart = ({ sensorId, data, period, onPeriodChange, selectedDate, onDateChange }) => {
    const { t } = useTranslation("global");
    const [isExporting, setIsExporting] = useState(false);
    const axiosPrivate = useAxiosPrivate();

    // Funzione per esportare i dati in Excel
    const handleExportToExcel = async () => {
        setIsExporting(true);
        try {
            const response = await axiosPrivate.get(
                `/sensors/${sensorId}/export`,
                {
                    params: {
                        period: period,
                        date: selectedDate
                    },
                    responseType: 'blob'
                }
            );

            // Scarica il file
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sensore_${sensorId}_${period}_${selectedDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Errore export Excel:', error);
            alert(t('sensors.chart.exportError', 'Errore durante l\'esportazione dei dati'));
        } finally {
            setIsExporting(false);
        }
    };

    // Caso 1: Dati non ancora caricati
    if (!data) {
        return (
            <Box sx={{ p: 3 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" gutterBottom>
                            {t('sensors.chart.loading', "Caricamento dati del sensore {{id}}...", { id: sensorId })}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Funzione per formattare le etichette in base al periodo
    const formatLabel = (timestamp) => {
        const date = new Date(timestamp);
        switch(period) {
            case 'day':
                return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
            case 'week':
                return date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
            case 'month':
                return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
            case 'year':
                return date.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
            default:
                return date.toLocaleTimeString('it-IT');
        }
    };

    // Caso 2: Nessun dato disponibile
    if (data.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Card>
                    <CardContent>
                        {/* Filtri periodo */}
                        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
                                <ToggleButtonGroup
                                    value={period}
                                    exclusive
                                    onChange={(e, newPeriod) => newPeriod && onPeriodChange(newPeriod)}
                                    size="small"
                                    sx={{ flexWrap: 'wrap' }}
                                >
                                    <ToggleButton value="day">
                                        {t('sensors.chart.filters.day', 'Giorno')}
                                    </ToggleButton>
                                    <ToggleButton value="week">
                                        {t('sensors.chart.filters.week', 'Settimana')}
                                    </ToggleButton>
                                    <ToggleButton value="month">
                                        {t('sensors.chart.filters.month', 'Mese')}
                                    </ToggleButton>
                                    <ToggleButton value="year">
                                        {t('sensors.chart.filters.year', 'Anno')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                
                                <TextField
                                    type="date"
                                    label={t('sensors.chart.filters.selectDate', 'Seleziona data')}
                                    value={selectedDate}
                                    onChange={(e) => onDateChange(e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                            
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportToExcel}
                                disabled={isExporting}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                {isExporting ? t('sensors.chart.exporting', 'Esportazione...') : t('sensors.chart.exportExcel', 'Esporta Excel')}
                            </Button>
                        </Box>

                        <Typography variant="h5" gutterBottom>
                            {t('sensors.chart.title', "Dati del sensore {{id}}", { id: sensorId })}
                        </Typography>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('sensors.chart.noData', "Nessun dato disponibile per il periodo selezionato. Il sensore Shelly H&T si risveglia ogni 2 ore o quando rileva cambiamenti significativi di temperatura/umidità.")}
                        </Alert>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Caso 3: Dati disponibili - mostra il grafico
    // Prepara i dati per il grafico
    const labels = data.map((entry) => formatLabel(entry.timestamp));
    const temperatureData = data.map((entry) => entry.temperature);
    const humidityData = data.map((entry) => entry.humidity);

    const chartData = {
        labels,
        datasets: [
            {
                label: t('sensors.chart.temperatureLabel', "Temperatura (°C)"),
                data: temperatureData,
                borderColor: "rgba(255, 99, 132, 1)",
                backgroundColor: "rgba(255, 99, 132, 0.2)",
                fill: true,
                tension: 0.4,
            },
            {
                label: t('sensors.chart.humidityLabel', "Umidità (%)"),
                data: humidityData,
                borderColor: "rgba(54, 162, 235, 1)",
                backgroundColor: "rgba(54, 162, 235, 0.2)",
                fill: true,
                tension: 0.4,
            },
        ],
    };

    // Titolo dinamico basato sul periodo
    const getPeriodTitle = () => {
        const date = new Date(selectedDate);
        switch(period) {
            case 'day':
                return date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            case 'week':
                return t('sensors.chart.weekOf', 'Settimana del {{date}}', { date: date.toLocaleDateString('it-IT') });
            case 'month':
                return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
            case 'year':
                return date.toLocaleDateString('it-IT', { year: 'numeric' });
            default:
                return '';
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Card>
                <CardContent>
                    {/* Filtri periodo */}
                    <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
                            <ToggleButtonGroup
                                value={period}
                                exclusive
                                onChange={(e, newPeriod) => newPeriod && onPeriodChange(newPeriod)}
                                size="small"
                            >
                                <ToggleButton value="day">
                                    {t('sensors.chart.filters.day', 'Giorno')}
                                </ToggleButton>
                                <ToggleButton value="week">
                                    {t('sensors.chart.filters.week', 'Settimana')}
                                </ToggleButton>
                                <ToggleButton value="month">
                                    {t('sensors.chart.filters.month', 'Mese')}
                                </ToggleButton>
                                <ToggleButton value="year">
                                    {t('sensors.chart.filters.year', 'Anno')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                            
                            <TextField
                                type="date"
                                label={t('sensors.chart.filters.selectDate', 'Seleziona data')}
                                value={selectedDate}
                                onChange={(e) => onDateChange(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ minWidth: 200 }}
                            />
                        </Box>
                        
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportToExcel}
                            disabled={isExporting}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            {isExporting ? t('sensors.chart.exporting', 'Esportazione...') : t('sensors.chart.exportExcel', 'Esporta Excel')}
                        </Button>
                    </Box>

                    <Typography variant="h5" gutterBottom>
                        {t('sensors.chart.title', "Dati del sensore {{id}}", { id: sensorId })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {getPeriodTitle()} - {t('sensors.chart.subtitle', "{{count}} letture registrate", { count: data.length })}
                    </Typography>
                    <Line
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                                legend: { 
                                    display: true,
                                    position: 'top',
                                },
                                tooltip: {
                                    mode: 'index',
                                    intersect: false,
                                }
                            },
                            scales: {
                                x: { 
                                    title: { 
                                        display: true, 
                                        text: t('sensors.chart.xAxis', "Tempo")
                                    },
                                    ticks: {
                                        maxRotation: 45,
                                        minRotation: 0
                                    }
                                },
                                y: { 
                                    title: { 
                                        display: true, 
                                        text: t('sensors.chart.yAxis', "Valore")
                                    },
                                    beginAtZero: false
                                },
                            },
                            interaction: {
                                mode: 'nearest',
                                axis: 'x',
                                intersect: false
                            }
                        }}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default SensorThChart;